import React, { useEffect } from "react";
import "@styles/main.scss";
import { useAppInitializer } from "@hooks/useAppInitializer";
import { useShouldAutoInitialize } from "@hooks/useShouldAutoInitialize";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { OverlaysProvider } from "contexts/OverlaysProvider";
import { SettingsProvider } from "@components/settings/SettingsProvider.provider";
import { PrivateInterface } from "@components/PrivateInterface";
import { PublicInterface } from "@components/PublicInterface";
import { useGAClientIdCookie } from "@hooks/useGAClientIdCookie";
import { useExternalRefererCookie } from "@hooks/useExternalRefererCookie";
import { useLandingPageCookie } from "@hooks/useLandingPageCookie";
import { useUser } from "@hooks/useUser";
import { ConnectAccountStateProvider } from "@components/connectAccount/ConnectAccountStateProvider";
import { NewVersionAvailableProvider } from "@components/newVersion/NewVersionAvailableProvider";
import { ToastContainer } from "@lib/toasts";
import { PageCoverLoader } from "@design-system/PageCoverLoader/PageCoverLoader";
import { OAuthPopupRoutes } from "@components/oauth/authorization/OAuthPopupRoutes";

export const App: React.FC = () => {
    useGAClientIdCookie();
    useExternalRefererCookie();
    useLandingPageCookie();

    if (window.location.pathname.startsWith("/oauth/")) {
        return <OAuthPopupRoutes />;
    }

    return <AppShell />;
};

const AppShell: React.FC = () => {
    const user = useUser();
    const { initialize, isInitialized } = useAppInitializer();
    const activeWorkspace = useActiveWorkspace();
    const shouldInitialize = useShouldAutoInitialize();

    useEffect(() => {
        if (isInitialized) {
            return;
        }

        if (shouldInitialize) {
            initialize().catch((err) => {
                throw err;
            });
        }
    }, [isInitialized, initialize, shouldInitialize]);

    const stateReloadingKey = [activeWorkspace?.id, user?.id].join("-");

    // The public pay page must render for anyone, including a logged-in Kick
    // user who opens a customer pay link — force the public interface for it.
    const isPublicPayPage =
        typeof window !== "undefined" &&
        window.location.pathname.startsWith("/pay/");

    return isInitialized || !shouldInitialize ? (
        <ConnectAccountStateProvider>
            <NewVersionAvailableProvider>
                {user && !isPublicPayPage ? (
                    <SettingsProvider>
                        <OverlaysProvider>
                            <PrivateInterface key={stateReloadingKey} />
                            <ToastContainer />
                        </OverlaysProvider>
                    </SettingsProvider>
                ) : (
                    <>
                        <PublicInterface />
                        <ToastContainer />
                    </>
                )}
            </NewVersionAvailableProvider>
        </ConnectAccountStateProvider>
    ) : (
        <PageCoverLoader />
    );
};
